import React from "react";
import { Link } from "react-router-dom";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { inr } from "../lib/api";
import { Minus, Plus, Trash2, Lock } from "lucide-react";

export default function CartPage() {
  const { cart, setQuantity, removeItem } = useCart();
  const { user } = useAuth();

  return (
    <div data-testid="cart-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <h1 className="font-heading text-5xl font-bold tracking-tight mb-12">Your cart</h1>
      {cart.items.length === 0 ? (
        <div className="bg-white border border-slate-100 rounded-3xl p-16 text-center">
          <p className="text-welljio-muted mb-6">Your cart is empty.</p>
          <Link to="/shop" className="inline-block bg-welljio-ink text-white rounded-full px-7 py-4 font-medium hover:bg-slate-800">Continue shopping</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            {cart.items.map(({ product, quantity, line_total }) => (
              <div key={product.id} className="bg-white border border-slate-100 rounded-3xl p-5 flex gap-4 items-center">
                <img src={product.images[0]} alt={product.name} className="w-24 h-24 rounded-2xl object-cover bg-welljio-tint" />
                <div className="flex-1 min-w-0">
                  <Link to={`/product/${product.slug}`} className="font-heading font-semibold hover:underline">{product.name}</Link>
                  <p className="text-xs text-welljio-muted">{product.category}</p>
                  <p className="text-sm mt-1">{inr(product.price)}</p>
                </div>
                <div className="flex items-center bg-welljio-tint rounded-full">
                  <button onClick={() => setQuantity(product.id, Math.max(0, quantity-1))} className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center"><Minus className="w-3 h-3" strokeWidth={1.5}/></button>
                  <span className="w-7 text-center text-sm">{quantity}</span>
                  <button onClick={() => setQuantity(product.id, quantity+1)} className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center"><Plus className="w-3 h-3" strokeWidth={1.5}/></button>
                </div>
                <p className="hidden sm:block w-24 text-right font-medium">{inr(line_total)}</p>
                <button onClick={() => removeItem(product.id)} className="text-welljio-subtle hover:text-welljio-ink"><Trash2 className="w-4 h-4" strokeWidth={1.5}/></button>
              </div>
            ))}
          </div>
          <aside className="bg-white border border-slate-100 rounded-3xl p-7 h-fit sticky top-28 space-y-3">
            <h2 className="font-heading text-xl font-semibold mb-3">Order summary</h2>
            <div className="flex justify-between text-sm text-welljio-muted"><span>Subtotal</span><span>{inr(cart.subtotal)}</span></div>
            <div className="flex justify-between text-sm text-welljio-muted"><span>Shipping</span><span>{cart.shipping === 0 ? "Free" : inr(cart.shipping)}</span></div>
            <div className="flex justify-between font-heading text-lg pt-3 border-t border-slate-100"><span>Total</span><span>{inr(cart.total)}</span></div>
            {user ? (
              <Link to="/checkout" data-testid="cart-page-checkout-button" className="block text-center w-full bg-welljio-ink text-white rounded-full py-4 font-medium hover:bg-welljio-coral transition-colors">Checkout</Link>
            ) : (
              <>
                <Link
                  to="/login"
                  state={{ from: "/checkout" }}
                  data-testid="cart-page-signin-checkout-button"
                  className="flex items-center justify-center gap-2 text-center w-full bg-welljio-ink text-white rounded-full py-4 font-medium hover:bg-welljio-coral transition-colors"
                >
                  <Lock className="w-4 h-4" strokeWidth={1.6} />
                  Sign in to checkout
                </Link>
                <p className="text-xs text-welljio-subtle text-center pt-1">
                  New here?{" "}
                  <Link to="/register" state={{ from: "/checkout" }} className="text-welljio-accent hover:underline">Create an account</Link>
                </p>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
